import { useMemo, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { ConfirmAvailability } from '@/components/ConfirmAvailability';
import { Input } from '@/components/Input';
import { Money } from '@/components/Money';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { COUNTER_CURRENCY as BRANCH_CURRENCY } from '@/features/counter/config';
import { money } from '@/lib/money';
import type { MenuItem } from './api';
import { Notice } from './parts';
import { useManageLive, useManageMenu, useSetAvailability } from './queries';
import { choicesSummary, describeManageError, groupRule } from './wording';
import { PageHeader } from '@/components/PageHeader';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';
import { SectionHeading } from '@/components/SectionHeading';

/** Prices are shown exactly as the server sent them. Nothing here adds, rounds or converts. */
function Price({ minor }: { minor: number }) {
  return <Money value={money(minor, BRANCH_CURRENCY)} className="text-sm font-bold text-ink" />;
}

function Choices({ item }: { item: MenuItem }) {
  if (item.modifier_groups.length === 0) return null;
  return (
    <details className="text-sm">
      <summary className="inline-flex min-h-10 cursor-pointer items-center font-semibold text-primary">
        {choicesSummary(item.modifier_groups)}
      </summary>
      <ul className="mt-1 flex flex-col gap-2">
        {item.modifier_groups.map((group) => (
          <li key={group.id} className="flex flex-col gap-0.5">
            <span className="font-medium text-ink">
              {group.name} <span className="font-normal text-faint">· {groupRule(group)}</span>
            </span>
            <ul className="flex flex-col gap-0.5 text-muted">
              {group.options.map((option) => (
                <li key={option.id} className="flex justify-between gap-3">
                  <span className="min-w-0 break-words">{option.name}</span>
                  {option.price_delta !== 0 && (
                    <span className="tabular-nums">
                      {option.price_delta > 0 ? '+' : '−'}
                      <Price minor={Math.abs(option.price_delta)} />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Turning an item off is confirmed with the same sheet the kitchen uses, so the words and the outcome are
 * identical on both screens. The request is the kitchen's availability call; a refusal is shown here.
 */
function ItemRow({ item }: { item: MenuItem }) {
  const set = useSetAvailability();
  const [confirming, setConfirming] = useState(false);
  return (
    <li className={cn('flex flex-col gap-2 px-4 py-3', !item.is_available && 'bg-surface/60')}>
      {/* The name has a line of its own with the price at its right end; the description sits under both. */}
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 text-base font-bold break-words text-ink">{item.name}</p>
        <span className="shrink-0">
          <Price minor={item.base_price} />
        </span>
      </div>
      {item.description && <p className="text-sm break-words text-muted">{item.description}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          {item.is_available ? (
            <Badge tone="success">Available</Badge>
          ) : (
            <Badge tone="neutral">Unavailable</Badge>
          )}
        </span>
        {item.is_available ? (
          <Button
            variant="secondary"
            loading={set.isPending}
            aria-label={`Mark ${item.name} unavailable`}
            onClick={() => setConfirming(true)}
          >
            Mark unavailable
          </Button>
        ) : (
          <Button
            variant="primary"
            loading={set.isPending}
            aria-label={`Make ${item.name} available`}
            onClick={() => set.mutate({ itemId: item.id, itemName: item.name, isAvailable: true })}
          >
            Make available
          </Button>
        )}
      </div>
      {set.isError && (
        <Notice tone="error">{describeManageError(set.error, 'availability')}</Notice>
      )}
      <Choices item={item} />
      {confirming && (
        <ConfirmAvailability
          itemName={item.name}
          onConfirm={() => {
            // Like the kitchen: the sheet closes at once; the row shows the request and any refusal.
            setConfirming(false);
            set.mutate({ itemId: item.id, itemName: item.name, isAvailable: false });
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </li>
  );
}

export function MenuPage() {
  useManageLive();
  const query = useManageMenu();
  const [search, setSearch] = useState('');
  const [unavailableOnly, setUnavailableOnly] = useState(false);

  const categories = useMemo(() => query.data?.categories ?? [], [query.data]);
  const all = useMemo(() => categories.flatMap((c) => c.items), [categories]);
  const unavailableCount = all.filter((i) => !i.is_available).length;

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return categories
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            (!unavailableOnly || !item.is_available) &&
            (needle === '' || item.name.toLowerCase().includes(needle)),
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [categories, search, unavailableOnly]);

  return (
    <section aria-labelledby="menu-title" className="flex flex-col gap-3">
      <PageHeader
        id="menu-title"
        title="Menu"
        subtitle={
          query.data && (
            <>
              {all.length} {all.length === 1 ? 'item' : 'items'} · {unavailableCount} unavailable
            </>
          )
        }
        actions={
          <Button
            variant="secondary"
            loading={query.isFetching && !query.isPending}
            onClick={() => void query.refetch()}
          >
            Refresh
          </Button>
        }
      />
      <p className="text-sm text-muted">
        Turn items off when they run out. Editing items and prices comes later.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-48 flex-1 md:max-w-sm">
          <Input
            label="Search items"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <Button
          variant="secondary"
          aria-pressed={unavailableOnly}
          className={unavailableOnly ? 'border-primary bg-primary-soft text-primary' : undefined}
          onClick={() => setUnavailableOnly((value) => !value)}
        >
          Unavailable only
        </Button>
      </div>

      {query.isPending && (
        <LoadingRegion slowHint label="Loading the menu">
          <div className="flex flex-col gap-4">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </LoadingRegion>
      )}
      {query.isError && !query.data && (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      )}
      {query.isError && query.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the menu. Showing the last menu received.
        </Notice>
      )}
      {query.data && all.length === 0 && (
        <EmptyState icon="menu" title="The menu is empty" message="This branch has no items." />
      )}
      {query.data && all.length > 0 && shown.length === 0 && (
        <EmptyState
          icon="menu"
          title={
            unavailableOnly && search.trim() === '' ? 'Everything is available' : 'No items match'
          }
          message={
            unavailableOnly && search.trim() === ''
              ? 'Nothing is turned off right now.'
              : 'Try a different search.'
          }
        />
      )}

      {/* Categories stack full width; inside each, items share two columns from desktop width. */}
      <div className="flex flex-col gap-4">
        {shown.map((category) => (
          <section
            key={category.id}
            aria-labelledby={`cat-${category.id}`}
            className="flex flex-col gap-2"
          >
            <SectionHeading id={`cat-${category.id}`} count={category.items.length}>
              {category.name}
            </SectionHeading>
            <ul
              className={cn(
                cardClasses({ padding: 'none' }),
                'grid gap-px overflow-hidden bg-line lg:grid-cols-2 [&>li]:bg-white',
              )}
            >
              {category.items.map((item) => (
                <ItemRow key={item.id} item={item} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </section>
  );
}
